/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
  Grid,
  Tag,
} from 'antd';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  FieldTimeOutlined,
  AlertOutlined,
  SearchOutlined,
  EllipsisOutlined,
  CloseCircleOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { onError } from 'utils/handlers';
import { isMutable } from 'utils/estadosGlobales';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import Previewer from 'components/Previewer';
import HistorialDePlacas, { permissionConfig as placasPermission } from 'components/Catalogos/HistorialDePlacas';
import DocumentosVehiculares, { permissionConfig as documentosVehicularesPermission } from 'components/Catalogos/DocumentosVehiculares';
import AlertasDelVehiculo, { permissionConfig as alertasDelVehiculoPermission } from 'components/Catalogos/AlertasDelVehiculo';
import {
  deleteVehiculo,
  getVehiculo,
  getVehiculos,
  patchVehiculo,
  postVehiculo,
} from 'api/recaudacion/vehiculos';
import ModalTramiteTarjeta from 'components/Catalogos/ModalTipoTramite';
import Moment from 'moment';
import { postObtenerReporte } from 'api/reporteador';
import RenderFieldsByConfig from 'views/Tramites/GPM/components/RenderFieldsByConfig';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { getConfiguracionesDePadrones } from 'api/configuracion/configuracionPadrones';
import { afectacionesEnum } from 'views/Tramites/GPM/enums';
import config from 'views/Tramites/GPM/configsPadrones/vehiculos';
import { getEstadosDeVehiculo, getServiciosDelVehiculo, getTiposDeVehiculos } from 'api/recaudacion';

const { useBreakpoint } = Grid;
const baseURI = 'recaudacion/vehiculos/';
const initialValues = [{ es_propietario_ciudadano: false }];
const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';

export const permissionConfig = {
  permissionModel: 'vehiculo',
  tabsModels: [
    placasPermission,
    documentosVehicularesPermission,
    alertasDelVehiculoPermission,
  ],
};

const Vehiculo = ({ permission, addressPermission }) => {
  const screen = useBreakpoint();
  const [form] = Form.useForm();
  const [formFactura] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formDocumentos] = Form.useForm();

  const es_propietario_ciudadano = Form.useWatch('es_propietario_ciudadano', form);
  const contribuyente_propietario = Form.useWatch('contribuyente_propietario', form);
  const propietario = Form.useWatch('propietario', form);

  const formInstances = [form, formInfo, formDocumentos, formFactura];

  const [visibleTarjeta, setVisibleTarjeta] = useState(false);
  const [configsPadron, setConfigsPadron] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [vehiculo, setVehiculo] = useState();
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [base64, setBase64] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [servicio, setServicio] = useState([]);
  const [estadoDelVehiculo, setEstadoDelVehiculo] = useState([]);
  const [tipoDeVehiculo, setTipoDeVehiculo] = useState([]);

  const mutable = isMutable(vehiculo);
  const basicKeys = ['general', 'detalles', 'info', 'repuve'];
  const complexForms = [{ key: 'direccion', formInstance: formAddress, permission: addressPermission }];
  const configAdd = configsPadron.find((c) => c.tipo_de_afectacion === afectacionesEnum.ALTA);
  const configEdit = configsPadron
    .find((c) => c.tipo_de_afectacion === afectacionesEnum.MODIFICACION);

  const configPadron = !selectedRowKeys.length ? configAdd : configEdit;

  const tabs = config.tabs
    .map((t, idx) => ({ ...t, formInstance: formInstances[idx], key: basicKeys[idx] }))
    .filter((t) => configPadron?.configuraciones_de_campos_de_padrones
      .some((f) => t.fields.includes(f.nombre_en_la_bd)));

  usePermissionMessage(permission);

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getVehiculos({ ...q, page });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    const fetchConfigsPadrones = async () => {
      const promises = [
        getConfiguracionesDePadrones({
          padron: 4,
          uso_de_configuracion_de_padron: 2,
        }),
        getEstadosDeVehiculo(),
        getTiposDeVehiculos(),
      ];
      if (!REPUVE) {
        promises.push(getServiciosDelVehiculo());
      }
      const [
        _configsPadron,
        _estados,
        _tipos,
        _servicios = [],
      ] = await Promise.all(promises);
      setConfigsPadron(_configsPadron);
      setServicio(_servicios);
      setEstadoDelVehiculo(_estados);
      setTipoDeVehiculo(_tipos);
      await fetchData();
    };

    fetchConfigsPadrones();
  }, []);

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onCancel = () => {
    setVisible(false);
    formInstances.forEach((formInstance) => { formInstance.resetFields(); });
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setVehiculo();
    setBase64();
  };

  const onClickEdit = configEdit ? async (response) => {
    setLoading(true);
    formInstances.forEach((formInstance) => formInstance.resetFields());
    const key = response?.id || selectedRowKeys[0];
    const _vehiculo = await getVehiculo(key);
    setLoading(false);
    if (!_vehiculo) return;
    formInstances.forEach((formInstance) => formInstance.setFieldsValue(_vehiculo));
    setVehiculo(_vehiculo);
    setVisible(true);
  } : () => message.warn('Falta configuración de padrón para este proceso');

  const setBaseAndShowPreview = (_base64) => {
    setBase64(_base64);
    setVisiblePreview(true);
  };

  const getReporteVehicular = async () => {
    setLoading(true);
    const current = data.results.find((item) => item.id === selectedRowKeys[0]);
    const values = {
      ID: 62,
      Parametros: [{
        Nombre: 'clave_del_vehiculo',
        TipoParametro: 'Texto',
        Valor: current.clave,
      }],
    };
    const response = await postObtenerReporte(values);
    setBaseAndShowPreview(response);
    setLoading(false);
  };

  const content = (
    <>
      <Row justify="end">
        <Button
          type="link"
          style={{ color: '#000000', marginTop: -25 }}
          icon={<CloseCircleOutlined />}
          onClick={() => message.destroy()}
        />
      </Row>
      <strong>Hace falta buscar un propietario.</strong>
      <br />
      <span>
        Para buscar un propietario ubiquese en la pestaña
        {' '}
        <Tag
          icon={<InfoCircleOutlined style={{ color: '#000000', margin: 0 }} />}
          onClick={() => {
            setCurrentTabKey('general');
            message.destroy();
          }}
        >
          Información General
        </Tag>
        seleccione a que padrón pertenece (Contribuyente o Ciudadano) y en el campo ubicado
        a la derecha introduzca algún dato del padrón correspondiente y presione el icono de
        búsqueda
        (
        <SearchOutlined style={{ color: '#000000', margin: 0 }} />
        ), en caso de que no se
        arroje alguna coincidencia presione el botón
        (
        <EllipsisOutlined style={{ color: '#000000', margin: 0 }} />
        )
        para realizar una búsqueda avanzada.
      </span>
    </>
  );

  const fieldsPropietario = ['q_contribuyente_propietario', 'q_propietario'];

  const validateForms = async ([curr, ...rest] = formInstances) => {
    if (!curr) return;
    await curr.validateFields();
    if (curr.__INTERNAL__.name === 'general') {
      await curr.validateFields(fieldsPropietario);
    }
    await validateForms(rest);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await validateForms();
      const values = formInstances.reduce((acum, curr) => ({ ...acum, ...curr.getFieldsValue() }));
      values[es_propietario_ciudadano ? 'contribuyente_propietario' : 'propietario'] = null;
      if (values?.exento_de_pago_de_tenencia) {
        values.tipo_de_tenencia_vehicular = null;
      }

      if (es_propietario_ciudadano ? !values.propietario : !values.contribuyente_propietario) {
        message.info({ content, duration: 0 });
        return false;
      }
      const [key] = selectedRowKeys;
      values.fecha_del_ultimo_movimiento = key ? new Moment() : null;
      const response = await (key ? patchVehiculo(key, values) : postVehiculo(values));
      if (response) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([response.id]);
            await onClickEdit(response);
            if (response?.comprobante) setBaseAndShowPreview(response.comprobante);
          } else {
            await onClickEdit(response);
          }
        } else {
          onCancel();
        }
      }
      setLoading(false);
      return true;
    } catch (err) {
      if (err.errorFields.some((e) => fieldsPropietario.includes(e.name[0]))) {
        message.info({ content, duration: 0 });
      }
      onError(err, setLoading, formInstances, setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteVehiculo(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setVehiculo(record);
    setSelectedRowKeys([record.id]);
    setTimeout(() => {
      form.setFieldsValue(record);
      formInfo.setFieldsValue(record);
      formDocumentos.setFieldsValue(record);
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = configAdd ? () => {
    onCancel();
    setVisible(true);
  } : () => message.warn('Falta configuración de padrón para este proceso');

  const onClickDelete = () => setVisibleAlert(true);

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'No. de Placa',
      dataIndex: 'numero_de_placa_vigente',
      key: 'numero_de_placa_vigente',
      width: 150,
    },
    {
      titleText: 'No. de Indentificación Vehícular',
      dataIndex: 'numero_de_identificacion_vehicular',
      key: 'numero_de_identificacion_vehicular',
      width: 250,
    },
    {
      titleText: 'Modelo',
      dataIndex: 'modelo',
      key: 'modelo',
      width: 100,
    },
    {
      titleText: 'Tarjeta de Circulación',
      dataIndex: 'tarjeta_de_circulacion',
      key: 'tarjeta_de_circulacion',
      width: 200,
    },
    {
      titleText: 'Clave de Repuve',
      dataIndex: 'clave_de_repuve',
      key: 'clave_de_repuve',
      width: 200,
    },
    !REPUVE && {
      titleText: 'Servicio',
      dataIndex: 'servicio',
      key: 'servicio',
      width: 200,
      catalog: {
        cat: servicio,
        labelProp: 'nombre',
        use: true,
      },
    },
    {
      titleText: 'Estatus del Vehìculo',
      dataIndex: 'estatus_del_vehiculo',
      key: 'estatus_del_vehiculo',
      width: 200,
      catalog: {
        cat: estadoDelVehiculo,
        labelProp: 'nombre',
        use: true,
      },
    },
    {
      titleText: 'Tipo de Vehìculo',
      dataIndex: 'tipo_de_vehiculo',
      key: 'tipo_de_vehiculo',
      width: 200,
      catalog: {
        cat: tipoDeVehiculo,
        labelProp: 'nombre',
        use: true,
      },
    },
  ];

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const allowSearch = screen.md || screen.lg || screen.xl || screen.xxl;

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible && (
          <Table
            cols={columns}
            data={data?.results}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            customActions={[
              {
                onClick: () => setVisibleTarjeta(true),
                icon: <PrinterOutlined />,
                text: 'Imprimir Tarjeta de Circulación',
                disabledBySelection: true,
              },
              {
                onClick: () => getReporteVehicular(),
                icon: <PrinterOutlined />,
                text: 'Reporte Auxiliar Histórico',
                disabledBySelection: true,
              },
            ]}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            // allowAdd={!!configAdd}
            // allowEdit={!!configEdit}
            rowKey="id"
            baseURI={baseURI}
            mobileColIndex={0}
            allowColumnSelector={false}
            allowSearch={allowSearch}
            permission={permission}
            pagination={false}
            paginationCallback={fetchData}
            count={data.count}
            currentPage={currentPage}
          />
        )}
        {visible && (
          <Col span={24}>
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Vehículo`}
              extra={(
                <FormSubmitControls
                  onFinish={getOnFinish()}
                  onCancel={onCancel}
                  mutable={mutable}
                  allowSaveAndContinue
                  onCancelText="Cancelar"
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Tabs
                onChange={mutable ? onChangeTabKey : setCurrentTabKey}
                activeKey={currentTabKey}
                type="card"
                items={[
                  ...tabs.map((tab, idx) => ({
                    label: (
                      <span>
                        {tab.title}
                        {' '}
                        {tab.icon}
                      </span>
                    ),
                    key: tab.key,
                    forceRender: true,
                    children: (
                      <Form
                        name={basicKeys[idx]}
                        layout="vertical"
                        form={tab.formInstance}
                        onFinish={() => onFinish()}
                        initialValues={initialValues[idx]}
                        scrollToFirstError
                      >
                        <RenderFieldsByConfig
                          disabled={!mutable}
                          fields={configPadron.configuraciones_de_campos_de_padrones
                            .filter((f) => tab.fields.includes(f.nombre_en_la_bd))}
                          padron={vehiculo}
                          setLoading={setLoading}
                          tipoDePadron={4}
                          tipoDeAfectacion={configPadron.tipo_de_afectacion}
                        />
                        <Form.Item hidden>
                          <Button htmlType="submit" />
                        </Form.Item>
                      </Form>
                    ),
                  })),
                  addressPermission?.hasAllViewPermission && {
                    label: (
                      <span>
                        <EnvironmentOutlined />
                        Dirección
                      </span>
                    ),
                    key: 'direccion',
                    forceRender: true,
                    disabled: !selectedRowKeys.length,
                    children: (
                      <Direccion
                        disabled={!mutable}
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        onError={onError}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        permission={addressPermission}
                        callback={(values, _continue) => {
                          if (!_continue) {
                            onCancel();
                          } else {
                            const [key] = selectedRowKeys;
                            const clone = [...data];
                            const mappedData = clone.map((e) => (e.id === key
                              ? ({ ...e, direccion: values?.id }) : e));
                            setData(mappedData);
                          }
                        }}
                      />
                    ),
                  },
                  permission.tabs?.placavehicular?.hasAllViewPermission && {
                    label: (
                      <span>
                        <FieldTimeOutlined />
                        Historial de Placas
                      </span>
                    ),
                    key: 'placas',
                    forceRender: !!selectedRowKeys.length,
                    disabled: !selectedRowKeys.length,
                    children: (
                      <HistorialDePlacas
                        vehiculoId={selectedRowKeys[0]}
                        permission={permission.tabs?.placavehicular}
                        esPropietarioCiudadano={es_propietario_ciudadano}
                        contribuyentePropietarioId={contribuyente_propietario}
                        ciudadanoPropietarioId={propietario}
                        setCurrentTabKey={setCurrentTabKey}
                      />
                    ),
                  },
                  permission.tabs?.documentodetramite?.hasAllViewPermission && {
                    label: (
                      <span>
                        <CameraOutlined />
                        Fotos
                      </span>
                    ),
                    key: 'documentos',
                    forceRender: !!selectedRowKeys.length,
                    disabled: !selectedRowKeys.length,
                    children: (
                      <DocumentosVehiculares
                        vehiculoId={selectedRowKeys[0]}
                        permission={permission.tabs?.documentodetramite}
                      />
                    ),
                  },
                  permission.tabs?.alerta?.hasAllViewPermission && {
                    label: (
                      <span>
                        <AlertOutlined />
                        Alertas
                      </span>
                    ),
                    key: 'alertas',
                    forceRender: !!selectedRowKeys.length,
                    disabled: !selectedRowKeys.length,
                    children: (<AlertasDelVehiculo vehiculoId={selectedRowKeys[0]} />),
                  },
                ].filter((e) => e)}
              />
            </Card>
          </Col>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Vehículo Seleccionado"
          loading={loading}
        />
        <Previewer
          visible={visiblePreview}
          setVisible={setVisiblePreview}
          base64={base64}
        />
        <ModalTramiteTarjeta
          visible={visibleTarjeta}
          setVisible={setVisibleTarjeta}
          selectedRowKey={selectedRowKeys[0]}
        />
      </Spin>
    </Row>
  );
};

export default Vehiculo;
